import { useState, useRef, useEffect } from 'react';
import { Play, Download, Trash2, Loader2, CheckCircle, Settings, ChevronDown, Package } from 'lucide-react';
import { ImageFile, ResizeSettings, BACKGROUND_REMOVAL_FORMATS } from '../types';
import ImageListItemCompact from './ImageListItemCompact';

interface ImageListCompactProps {
  images: ImageFile[];
  settings: ResizeSettings;
  isProcessing: boolean;
  onProcessImage: (image: ImageFile) => void;
  onProcessAll: (imagesToProcess: ImageFile[]) => void;
  onDownloadImage: (image: ImageFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveImage: (id: string) => void;
  onClearAll: () => void;
  onUpdateSettings: (key: keyof ResizeSettings, value: any) => void;
  isDarkMode: boolean;
}

export default function ImageListCompact({
  images,
  settings,
  isProcessing,
  onProcessImage,
  onProcessAll,
  onDownloadImage,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveImage,
  onClearAll,
  onUpdateSettings,
  isDarkMode
}: ImageListCompactProps) {
  const [showFormatOptions, setShowFormatOptions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFormatOptions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (images.length === 0) {
    return null;
  }

  const pendingImages = images.filter(image => image.status === 'pending');
  const processingImages = images.filter(image => image.status === 'processing');
  const completedImages = images.filter(image => image.status === 'completed');

  const handleProcessAll = () => {
    console.log('🚀 BOTÃO CONVERTER TODOS CLICADO (ImageListCompact)!');
    console.log('📊 Estado atual no ImageListCompact:', {
      totalImages: images.length,
      pendingImages: pendingImages.length,
      isProcessing,
      images: images.map(img => ({
        id: img.id,
        name: img.file?.name,
        status: img.status,
        hasFile: !!img.file
      }))
    });
    onProcessAll(images);
  };

  return (
    <div className="w-full space-y-6">
      {/* Header com Controles */}
      <div className={`${
        isDarkMode ? 'bg-[#2A2E3F] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
      } border rounded-2xl p-4 mb-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Lado Esquerdo: Título e Estatísticas */}
          <div className="flex flex-wrap items-center gap-3">
            <h3 className={`flex items-center text-xl font-semibold ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              <span>Imagens Carregadas</span>
              <span className="lg:hidden ml-2 text-sm px-2 py-0.5 rounded-md bg-[#1f2937] text-[#60a5fa]">{images.length} total</span>
            </h3>
            
            {/* Estatísticas Compactas - visíveis apenas no Desktop */}
            <div className="hidden lg:flex flex-wrap items-center gap-2 text-xs">
              <span className={`flex items-center space-x-1 px-2 py-1 rounded-full ${
                isDarkMode ? 'text-blue-400 bg-blue-500/10' : 'text-blue-600 bg-blue-100'
              }`}>
                <span>{images.length} total</span>
              </span>
              {processingImages.length > 0 && (
                <span className={`flex items-center space-x-1 px-2 py-1 rounded-full ${
                  isDarkMode ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-600 bg-yellow-100'
                }`}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>{processingImages.length} processando</span>
                </span>
              )}
              {completedImages.length > 0 && (
                <span className={`flex items-center space-x-1 px-2 py-1 rounded-full ${
                  isDarkMode ? 'text-green-400 bg-green-500/10' : 'text-green-600 bg-green-100'
                }`}>
                  <CheckCircle className="w-3 h-3" />
                  <span>{completedImages.length} concluído{completedImages.length !== 1 ? 's' : ''}</span>
                </span>
              )}
            </div>
          </div>
          
          {/* Lado Direito: Controles e Ações */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Select de Formato de Saída */}
            <div className="hidden" ref={dropdownRef}>
              <button
                onClick={() => setShowFormatOptions(!showFormatOptions)}
                className={`flex items-center space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] ${
                  isDarkMode 
                    ? 'bg-[#383D52] border-[#4A4F64] hover:bg-[#4A4F64] text-[#EAECF3]'
                    : 'bg-white border-[#E5E5E5] hover:bg-[#F8F9FA] text-[#4A4A69]'
                } border shadow-sm hover:shadow-md relative touch-manipulation active:scale-95`}
              >
                <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{settings.format.toUpperCase()}</span>
                <span className="sm:hidden">{settings.format.substring(0, 3).toUpperCase()}</span>
                
                <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-200 ${
                  showFormatOptions ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Dropdown de Formatos */}
              {showFormatOptions && (
                <div className={`absolute top-full right-0 mt-2 w-72 sm:w-80 ${
                  isDarkMode ? 'bg-[#2A2E3F] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
                } border rounded-xl shadow-lg z-50 p-3 sm:p-4 max-h-[70vh] overflow-y-auto`}>
                  <div className="space-y-3 sm:space-y-4">
                    {/* Seletor de Formato */}
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                      }`}>
                        Formato de Saída
                      </label>
                      <select
                        value={settings.format}
                        onChange={(e) => onUpdateSettings('format', e.target.value)}
                        className={`w-full px-2 sm:px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm ${
                          isDarkMode 
                            ? 'bg-[#383D52] border-[#4A4F64] text-[#EAECF3]' 
                            : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                        }`}
                      >
                        <optgroup label="🔄 Padrão">
                          <option value="original">Manter formato original</option>
                        </optgroup>
                        
                        <optgroup label="🌐 Formatos Web">
                          <option value="jpeg">JPEG - Compressão alta</option>
                          <option value="png">PNG - Com transparência</option>
                          <option value="webp">WebP - Moderno e otimizado</option>
                          <option value="avif">AVIF - Nova geração</option>
                          <option value="gif">GIF - Animações</option>
                        </optgroup>
                        
                        <optgroup label="📱 Formatos Mobile">
                          <option value="heic">HEIC - Apple (iOS)</option>
                          <option value="heif">HEIF - Alta eficiência</option>
                        </optgroup>
                        
                        <optgroup label="🖨️ Formatos Profissionais">
                          <option value="tiff">TIFF - Qualidade máxima</option>
                          <option value="bmp">BMP - Windows Bitmap</option>
                          <option value="svg">SVG - Vetorial</option>
                          <option value="psd">PSD - Photoshop</option>
                        </optgroup>
                        
                        <optgroup label="🎮 Formatos Especializados">
                          <option value="dds">DDS - DirectDraw Surface</option>
                          <option value="hdr">HDR - Alto contraste</option>
                          <option value="exr">EXR - OpenEXR (Cinema)</option>
                          <option value="ico">ICO - Ícone Windows</option>
                        </optgroup>
                        
                        <optgroup label="🔧 Formatos Técnicos">
                          <option value="tga">TGA - Truevision</option>
                          <option value="pcx">PCX - PiCture eXchange</option>
                          <option value="jp2">JP2 - JPEG 2000</option>
                          <option value="jbig">JBIG - Bi-level</option>
                          <option value="pbm">PBM - Portable Bitmap</option>
                          <option value="pgm">PGM - Portable Graymap</option>
                          <option value="ppm">PPM - Portable Pixmap</option>
                        </optgroup>
                        
                        <optgroup label="📼 Formatos Legados">
                          <option value="xbm">XBM - X11 Bitmap</option>
                          <option value="xpm">XPM - X11 Pixmap</option>
                          <option value="sun">SUN - Sun Raster</option>
                          <option value="sgi">SGI - Silicon Graphics</option>
                          <option value="ras">RAS - Sun Raster</option>
                          <option value="pct">PCT - Macintosh PICT</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Controle de Qualidade */}
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                      }`}>
                        Qualidade: {Math.round(settings.quality * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.1"
                        value={settings.quality}
                        onChange={(e) => onUpdateSettings('quality', parseFloat(e.target.value))}
                        className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                        }`}
                      />
                      <div className={`flex justify-between text-xs mt-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span>Baixa</span>
                        <span>Alta</span>
                      </div>
                    </div>

                    {/* Opção de Remoção de Fundo - Mostrar apenas para formatos suportados */}
                    {BACKGROUND_REMOVAL_FORMATS.includes(settings.format as any) && (
                      <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col space-y-1">
                          <span className={`text-sm font-medium ${
                            isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                          }`}>
                            Remover Fundo
                          </span>
                          <span className={`text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Aplicar em todas as imagens
                          </span>
                        </div>
                        <button
                          onClick={() => onUpdateSettings('removeBackground', !settings.removeBackground)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            settings.removeBackground ? 'bg-purple-500' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`${
                              settings.removeBackground ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 w-full md:w-auto">
              {/* Converter Todos - Mobile/Tablet (sempre visível) */}
              <button
                onClick={handleProcessAll}
                className="flex-1 md:flex-initial flex lg:hidden items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                title="Converter todos"
              >
                <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Converter Todos</span>
              </button>

              {completedImages.length > 0 && (
                <>
                  <button
                    onClick={onDownloadAll}
                    className="hidden lg:flex flex-1 md:flex-initial items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                    title={`Baixar todos (${completedImages.length})`}
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Baixar Todos</span>
                    <span className="text-xs opacity-75">({completedImages.length})</span>
                  </button>
                  
                  <button
                    onClick={onDownloadAllAsZip}
                    className="hidden lg:flex flex-1 md:flex-initial items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                    title="Baixar como ZIP"
                  >
                    <Package className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>ZIP</span>
                  </button>
                </>
              )}
              
              {pendingImages.length > 0 && !isProcessing && (
                <button
                  onClick={handleProcessAll}
                  className="hidden lg:flex flex-1 md:flex-initial items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                  title={`Converter todos (${pendingImages.length})`}
                >
                  <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Converter Todos</span>
                  <span className="text-xs opacity-75">({pendingImages.length})</span>
                </button>
              )}
              
              <button
                onClick={onClearAll}
                className={`flex-1 md:flex-initial flex items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 ${
                  isDarkMode ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300' : 'bg-red-100 hover:bg-red-200 text-red-600'
                } rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md`}
                title="Limpar tudo"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Limpar Tudo</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Imagens */}
      <div className="space-y-3">
        {images.map((image) => (
          <ImageListItemCompact
            key={image.id}
            image={image}
            onProcessImage={onProcessImage}
            onDownloadImage={onDownloadImage}
            onRemoveImage={onRemoveImage}
            isProcessing={isProcessing}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>
    </div>
  );
} 